import { produce } from "immer";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type FlyoutProps } from "../../../utils/flyout-props";
import {
  defaultFlyoutPostContextState,
  type FlyoutPostContentEntry,
  FlyoutPostStateContext,
} from "./context-flyout-post";

type FlyoutPostStateProviderProps = {
  screen: FlyoutProps["screen"];
};

export function FlyoutPostStateProvider({
  screen,
  children,
}: PropsWithChildren<FlyoutPostStateProviderProps>) {
  const assets = useMemo<FlyoutPostContentEntry[]>(() => {
    if (Array.isArray(screen.flyout?.post?.customData?.flyoutPost?.contents)) {
      return screen.flyout.post.customData.flyoutPost.contents;
    }
    return [];
  }, [screen.flyout?.post?.customData?.flyoutPost?.contents]);

  const [state, setState] = useState({
    ...defaultFlyoutPostContextState,
    assets,
  });

  const incrementAssetIdx = useCallback(() => {
    setState((prev) =>
      produce(prev, (draft) => {
        if (prev.assets.length === 0) {
          draft.idxAsset = 0;
        } else if (prev.idxAsset >= prev.assets.length - 1) {
          draft.idxAsset = 0;
        } else {
          draft.idxAsset = prev.idxAsset + 1;
        }
      }),
    );
  }, []);
  const decrementAssetIdx = useCallback(() => {
    setState((prev) =>
      produce(prev, (draft) => {
        if (prev.assets.length === 0) {
          draft.idxAsset = 0;
        } else if (prev.idxAsset <= 0) {
          draft.idxAsset = prev.assets.length - 1;
        } else {
          draft.idxAsset = prev.idxAsset - 1;
        }
      }),
    );
  }, []);

  useEffect(
    function effectUpdateState() {
      setState({
        ...defaultFlyoutPostContextState,
        assets,
      });
    },
    [assets],
  );

  return (
    <FlyoutPostStateContext.Provider
      value={{
        ...state,
        incrementAssetIdx,
        decrementAssetIdx,
      }}
    >
      {children}
    </FlyoutPostStateContext.Provider>
  );
}
